import { useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useHubspotIntegration } from "@/features/settings/use-hubspot-integration";
import type { HubspotObjectLinkingRow } from "@/features/settings/use-hubspot-integration";
import { useUpdateHubspotLinking } from "@/features/settings/use-update-hubspot-linking";

// SPA port of /app/admin/settings/integrations/_components/HubSpotObjectLinkingTable.tsx.

export function HubSpotObjectLinkingTable() {
  const query = useHubspotIntegration();
  const rows = query.data ?? [];

  if (query.isLoading) {
    return (
      <p className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
        No object-linking rows yet. Apply migration 0052 to seed the three
        deal-relationship typeIds (52 / 54 / 62).
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Object</th>
            <th className="px-3 py-2 text-left">typeId</th>
            <th className="px-3 py-2 text-left">HubSpot label</th>
            <th className="px-3 py-2 text-left">Attribution name</th>
            <th className="px-3 py-2 text-right">Enabled</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => (
            <Row key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ row }: { row: HubspotObjectLinkingRow }) {
  const mutation = useUpdateHubspotLinking();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(row.attribution_name);

  function save() {
    mutation.mutate(
      {
        id: row.id,
        attribution_name: draftName.trim(),
        enabled: row.enabled,
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  function cancel() {
    setDraftName(row.attribution_name);
    setEditing(false);
  }

  function toggleEnabled(next: boolean) {
    // Toggle persists immediately — feels natural for a switch. The
    // attribution name still requires explicit save (Pencil → Check).
    mutation.mutate({
      id: row.id,
      attribution_name: row.attribution_name,
      enabled: next,
    });
  }

  const pending = mutation.isPending;
  return (
    <tr className={row.enabled ? "" : "opacity-50"}>
      <td className="px-3 py-2 capitalize">{row.crm_object_type}</td>
      <td className="px-3 py-2 font-mono text-xs">{row.association_type_id}</td>
      <td className="px-3 py-2 text-muted-foreground">{row.association_label}</td>
      <td className="px-3 py-2">
        {editing ? (
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="h-7 text-xs"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
          />
        ) : (
          <span className="font-medium text-foreground">
            {row.attribution_name}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Switch
          checked={row.enabled}
          onCheckedChange={toggleEnabled}
          disabled={pending && !editing}
        />
      </td>
      <td className="px-3 py-2 text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={cancel}
              disabled={pending}
              className="h-7 px-2"
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={pending || !draftName.trim()}
              className="h-7 px-2"
              title="Save"
            >
              {pending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            className="h-7 px-2"
            title="Edit attribution name"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </td>
    </tr>
  );
}
